// src/components/form/DynamicUserForm.js
/**
 * DynamicUserForm
 * ---------------
 * Multi-step questionnaire.
 *
 * Responsibilities
 * • Filters the global `questions` array against current answers + user data.
 * • Generates a step list (`single`, `group`, `review`) via `buildSteps()`.
 * • Performs per-step validation before allowing Next.
 * • On the final “Confirm” step:
 *      – persists answers through the protected regional backend
 *      – updates the in-app profile cache for downstream screens
 * • Redirects to the requested recording path once data is safely persisted.
 *
 * Notes
 * • Pure UI logic is delegated to smaller components/helpers so this file
 *   stays focused on orchestration.
 * • Hook calls are grouped by purpose: derived state → effects → handlers.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { getAuth } from "firebase/auth";

import { useAuth } from "../../context/AuthContext";
import { firebaseApp } from "../../firebase/firebaseApp";

import QuestionStep from "./QuestionStep";
import GroupedQuestionStep from "./GroupedQuestionStep";
import ReviewStep from "./ReviewStep";

import { questions } from "../../utils/form/formQuestions";
import {
  getQuestionValidationResult,
  isQuestionValid,
} from "../../utils/form/isQuestionValid";
import {
  buildSteps,
  filterQuestions,
  rangeDefaults,
} from "../../utils/form/formRuntime";
import { questionUsesFreeText } from "../../utils/form/formSelection";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { uploadMetadata } from "../../utils/firebase/uploadMetadata";
import FormNavButtons from "./FormNavButtons";
import { useTranslation } from "react-i18next";
import { useProfilePrefetch } from "../../context/ProfilePrefetchContext";
import { useRegionalBackend } from "../../context/RegionalBackendContext";
import ErrorPopup from "../ErrorPopup";
import { normalizeProfileFormData } from "../../utils/validation/controlledInputs";
import { markTodoIntroPending } from "../../utils/tasks/todoIntroPrompt";
import { scrollAppContentToTop } from "../../utils/navigation/appScroll";
/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */
/**
 * @param {{ initialData?: Record<string, any>, returnTo?: string }} props
 */
function DynamicUserForm({ initialData = {}, returnTo = "/home" }) {
  const translationForm = "form";
  const { t: tCommon } = useTranslation();
  const { t: tForm } = useTranslation(translationForm);

  const { user } = useAuth();
  const regionalBackend = useRegionalBackend();
  const navigate = useNavigate();
  const effectiveUser = useMemo(
    () => ({
      ...user,
      returning:
        regionalBackend.profileSummary?.returning ?? user?.returning ?? false,
    }),
    [regionalBackend.profileSummary?.returning, user],
  );

  /* ----------------------------- state ----------------------------- */

  const [popup, setPopup] = useState(null);
  const [invalidQuestionIds, setInvalidQuestionIds] = useState([]);
  const closePopup = useCallback(() => setPopup(null), []);

  /**
   * Whether this submission is the user's first ever profile submission, and so
   * earns the To do introduction on Home.
   *
   * Captured from the first *resolved* backend profile summary, not from the
   * first render: `profileSummaryLoading` starts true, and a null summary during
   * bootstrap looks identical to a brand-new user, which would hand returning
   * users an introduction they have already had. Held in a ref because the
   * answer must reflect the state *before* this submission overwrites it.
   */
  const todoIntroEligibleRef = useRef(null);

  useEffect(() => {
    if (todoIntroEligibleRef.current !== null) return;
    if (regionalBackend.profileSummaryLoading) return;

    todoIntroEligibleRef.current =
      effectiveUser.returning === false &&
      !regionalBackend.profileSummary?.latestMs;
  }, [
    effectiveUser.returning,
    regionalBackend.profileSummary?.latestMs,
    regionalBackend.profileSummaryLoading,
  ]);

  // ---------- initial answers ----------
  const [formData, setFormData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);
  const questionById = useMemo(
    () => new Map(questions.map((question) => [question.id, question])),
    [],
  );
  const questionIdByFreeTextFieldId = useMemo(
    () =>
      new Map(
        questions
          .filter((question) => question.freeTextFieldId)
          .map((question) => [question.freeTextFieldId, question.id]),
      ),
    [],
  );
  const visibilityBaseline = useMemo(() => initialData ?? {}, [initialData]);

  /* --------------------------- selectors --------------------------- */
  const filtered = useMemo(
    () =>
      filterQuestions(questions, formData, effectiveUser, {
        visibilityData: visibilityBaseline,
      }),
    [effectiveUser, formData, visibilityBaseline],
  );
  const steps = useMemo(() => buildSteps(filtered), [filtered]);
  const totalSteps = steps.length;
  const progress = (currentStep / totalSteps) * 100;

  const { setProfile } = useProfilePrefetch();

  /* --------- start each step at the top of the scroll area --------- */
  // Advancing a step swaps the visible content without changing the route, so
  // the shared container keeps the previous step's scroll offset and a long
  // step drops the user part-way into the next one.
  useEffect(() => {
    scrollAppContentToTop();
  }, [currentStep]);

  /* ------------------- initialise range defaults ------------------ */
  useEffect(() => {
    const defaults = rangeDefaults(filtered, formData);
    if (Object.keys(defaults).length) {
      setFormData((p) => ({ ...p, ...defaults }));
    }
  }, [filtered, formData]);

  /* --------------------------- handlers --------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      const question = questionById.get(name);

      if (
        question?.freeTextFieldId &&
        !questionUsesFreeText(question, value)
      ) {
        next[question.freeTextFieldId] = "";
      }

      return next;
    });
    setInvalidQuestionIds((prev) =>
      prev.filter(
        (questionId) =>
          questionId !== name &&
          questionId !== questionIdByFreeTextFieldId.get(name),
      ),
    );
  };

  const handleNext = useCallback(
    /** @param {React.FormEvent | undefined} e */
    async (e) => {
      e?.preventDefault();

      /* ── Final step → persist answers ───────────────────────────── */
      if (currentStep === totalSteps - 1) {
        try {
          const { currentUser } = getAuth(firebaseApp);
          if (!currentUser) throw new Error("No authenticated user");
          const normalizedProfile = normalizeProfileFormData(formData);

          // Valid → persist
          await uploadMetadata(currentUser.uid, normalizedProfile);
          regionalBackend.applyProfile?.(normalizedProfile);
          setProfile(currentUser.uid, normalizedProfile);
          const refreshRequest = regionalBackend.refresh?.();
          if (refreshRequest?.catch) {
            void refreshRequest.catch((error) => {
              console.error("Regional backend profile refresh failed:", error);
            });
          }

          try {
            sessionStorage.removeItem("formMetadata");
          } catch {}

          // First onboarding run: hand the To do introduction to the Home page
          // rather than interrupting the redirect out of the form.
          if (todoIntroEligibleRef.current) {
            markTodoIntroPending(currentUser.uid);
          }

          navigate(returnTo, { replace: true });
          return;
        } catch (err) {
          console.error("Metadata upload failed:", err);
          if (user?.uid) setProfile(user.uid, formData);
          return;
        }
      }

      /* ── Validate current step before advancing ─────────────────── */
      const step = steps[currentStep];
      const stepIsValid =
        step.type === "single"
          ? isQuestionValid(step.question, formData)
          : step.type === "group"
            ? !step.questions.some((q) => !isQuestionValid(q, formData))
            : true;

      if (!stepIsValid) {
        const invalidStepResults =
          step.type === "single"
            ? [
                {
                  question: step.question,
                  result: getQuestionValidationResult(step.question, formData),
                },
              ]
            : step.type === "group"
              ? step.questions
                  .map((question) => ({
                    question,
                    result: getQuestionValidationResult(question, formData),
                  }))
                  .filter(({ result }) => !result.valid)
              : [];
        const invalidStepResult = invalidStepResults[0] ?? null;
        const validationResult = invalidStepResult?.result ?? null;
        const invalidQuestion = invalidStepResult?.question ?? null;
        const messageKey = validationResult?.messageKey || "form.reqFieldsAlert";
        const missingFieldSummaries = invalidStepResults
          .filter(({ result }) => result?.messageKey === "form.reqFieldsAlert")
          .map(({ question }) => ({
            id: question.id,
            label: question.titleKey
              ? tForm(question.titleKey)
              : (question.title ?? question.id),
          }));
        const missingFieldLabel = invalidQuestion
          ? invalidQuestion.titleKey
            ? tForm(invalidQuestion.titleKey)
            : (invalidQuestion.title ?? invalidQuestion.id)
          : null;
        const missingQuestionIds = missingFieldSummaries.map(({ id }) => id);

        setInvalidQuestionIds(missingQuestionIds);

        setPopup({
          message:
            messageKey === "form.reqFieldsAlert" && missingFieldLabel ? (
              <span className="block">
                <span className="block">{tCommon("form.reqFieldsAlert")}</span>
                {missingFieldSummaries.length > 1 ? (
                  <ul className="mt-2 list-disc pl-5 font-semibold text-primary">
                    {missingFieldSummaries.map(({ id, label }) => (
                      <li key={id}>{label}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="mt-1 block font-semibold text-primary">
                    {missingFieldLabel}
                  </span>
                )}
              </span>
            ) : (
              tCommon(messageKey)
            ),
        });
        return;
      }

      setInvalidQuestionIds([]);
      setCurrentStep((s) => s + 1);
    },
    [
      currentStep,
      totalSteps,
      formData,
      navigate,
      setProfile,
      regionalBackend,
      user?.uid,
      tCommon,
      tForm,
      steps,
      returnTo,
    ],
  );

  const handlePrev = (e) => {
    e.preventDefault();
    setInvalidQuestionIds([]);
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  /* ------------- auto-fill when returning user -------------------- */
  useEffect(() => {
    if (effectiveUser.returning) {
      const stored = sessionStorage.getItem("autoFillData");
      if (stored) setFormData((p) => ({ ...p, ...JSON.parse(stored) }));
    }
  }, [effectiveUser.returning]);

  /* ---- edge case: zero questions after filtering → skip form ----- */
  useEffect(() => {
    if (!filtered.length) {
      navigate(returnTo);
    }
  }, [filtered, formData, navigate, returnTo]);

  /* ------------------------ render helpers ------------------------ */
  const renderStep = () => {
    const step = steps[currentStep];
    switch (step.type) {
      case "single":
        return (
          <QuestionStep
            question={step.question}
            value={formData[step.question.id]}
            formData={formData}
            onChange={handleChange}
            autoFocus
            onEnter={handleNext}
            hasValidationError={invalidQuestionIds.includes(step.question.id)}
            translationForm={translationForm}
          />
        );
      case "group":
        return (
          <GroupedQuestionStep
            questions={step.questions}
            formData={formData}
            onChange={handleChange}
            onEnter={handleNext}
            invalidQuestionIds={invalidQuestionIds}
            translationForm={translationForm}
          />
        );
      default:
        return (
          <ReviewStep
            questions={filtered}
            formData={formData}
            translationForm={translationForm}
          />
        );
    }
  };

  /* ------------------------------ UI ------------------------------ */
  return (
    <div className="user-form p-4 w-full mx-auto flex flex-col justify-between gap-4">
      <ErrorPopup popup={popup} onClose={closePopup} />
      {/* progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        className="rounded-full"
        sx={{
          backgroundColor: "#B776B9",
          "& .MuiLinearProgress-bar": { backgroundColor: getPrimaryColour() },
        }}
      />

      {renderStep()}

      {/* nav buttons */}
      <FormNavButtons
        hasPrev={currentStep > 0}
        onPrev={handlePrev}
        onNext={handleNext}
        isLast={currentStep === totalSteps - 1}
      />
    </div>
  );
}

export default DynamicUserForm;
